/**
 * @module lib/commercial/keytar/cannabis-sponsored-content/pages
 */
import { session } from "lib/store";
import { calculateAge, isValidDay, isValidMonth, isValidYear, qs, qsa, getById } from "utils/utils";
import logger from "utils/logger";
import ee from "lib/eventemitter";

const debugTag = "[KEYTAR - Cannabis Sponsored Content]";

// Note: these IDs are duplicated at resources/scripts/test/pages/ageGate.js as they weren't importable
// from the test directory. If these get changed, please update the test file as well.
const userInfoBlurbId = "arc-c-age-gate-user-info-blurb";
const confirmBlurbId = "arc-c-age-gate-confirm-blurb";
const userInfoFormId = "arc-c-age-gate";
const confirmFormId = "arc-c-age-gate-confirm";
const underAgePgId = "arc-c-age-gate-under-age";

let userDateOfBirth = null;
let userProvince = null;
let values = null; // form values
let pages = null;

const confirmedKey = "tgam.keytar.age-gate.confirm";

/**
 * Returns session storage item value for confirmedKey.
 * @returns {Boolean}
 */
export function getIsConfirmed() {
  return session.get(`${confirmedKey}`) || false;
}

/**
 * Displays step, `step`, of Age Gate wall.
 * @param {String} step - step of Age Gate wall to show.
 */
function showStep(step) {
  if (step === "confirm") {
    pages.userInfo.blurb.classList.add("u-hidden");
    pages.userInfo.form.classList.add("u-hidden");
    pages.confirm.form.classList.remove("u-hidden");
    pages.confirm.blurb.classList.remove("u-hidden");

    // Replace placeholders with birthdate and province submitted from step 1.
    getById(`${confirmFormId}-user-dob`).innerHTML = userDateOfBirth;
    getById(`${confirmFormId}-user-province`).innerHTML = userProvince;

    pages.confirm.fields.confirmed.focus();

    logger.info(`${debugTag} Birthday: ${userDateOfBirth}`);
    logger.info(`${debugTag} Province: ${userProvince}`);
    logger.info(`${debugTag} >= 19 years old`);
  } else if (step === "underAge") { // Not of legal age, show blurb.
    pages.userInfo.blurb.classList.add("u-hidden");
    pages.userInfo.form.classList.add("u-hidden");
    pages.underAge.blurb.classList.remove("u-hidden");
    logger.info(`${debugTag} < 19 years old`);
  } else if (step === "reenterDob") { // Re-enter birth date after initial <=19 years answer.
    pages.underAge.blurb.classList.add("u-hidden");
    pages.userInfo.blurb.classList.remove("u-hidden");
    pages.userInfo.form.classList.remove("u-hidden");
    pages.userInfo.fields.day.focus();
  }
}

/**
 * Validates form and returns whether or not form has errors.
 * @param {String} form - form to be validated
 * @returns {Boolean}
 */
function validateForm(form) {
  let isUserInfoForm = qs(`#${userInfoFormId}-province`, form) !== null;

  logger.info(`${debugTag} isUserInfoForm ${isUserInfoForm}`);

  if (isUserInfoForm) { // Validate user info form
    let isValidDayVal = null;
    let isValidMonthVal = null;
    let isValidYearVal = null;

    values = {
      province: pages.userInfo.fields.province.value,
      day: pages.userInfo.fields.day.value.trim(),
      month: pages.userInfo.fields.month.value.trim(),
      year: pages.userInfo.fields.year.value.trim(),
    };

    isValidDayVal = isValidDay(values.day);
    isValidMonthVal = isValidMonth(values.month);
    isValidYearVal = isValidYear(values.year);

    logger.info(`${debugTag} Valid Date? Day: ${isValidDayVal}. Month: ${isValidMonthVal}. Year: ${isValidYearVal}`);

    if (!isValidDayVal) {
      pages.userInfo.fields.day.parentElement.classList.add("c-form__field--error");
    }

    if (!isValidMonthVal) {
      pages.userInfo.fields.month.parentElement.classList.add("c-form__field--error");
    }

    if (!isValidYearVal) {
      pages.userInfo.fields.year.parentElement.classList.add("c-form__field--error");
    }

    if (!isValidDayVal || !isValidMonthVal || !isValidYearVal) {
      qs(".c-form__error", pages.userInfo.fields.day.parentElement.parentElement.parentElement).removeAttribute("hidden");
      pages.userInfo.state = "error";
    }
  } else { // Validate confirmation form
    values = {
      confirm: pages.confirm.fields.confirmed.checked
    };

    if (!values.confirm) {
      pages.confirm.fields.confirmed.parentElement.parentElement.classList.add("c-form__field--error");
      qs(".c-form__error", pages.confirm.fields.confirmed.parentElement.parentElement).removeAttribute("hidden");
      pages.confirm.state = "error";
    }
  }

  if (pages.userInfo.state === "error" || pages.confirm.state === "error") {
    qs(".c-form__global-error", form).removeAttribute("hidden");
    return true;
  }

  return false;
}

/**
 * Resets form errors
 * @param {String} form - form whose errors are to be reset
 */
function resetFormErrors(form) {
  qs(".c-form__global-error", form).setAttribute("hidden", "hidden");
  qsa(".c-form__error", form).forEach(el => {
    el.setAttribute("hidden", "hidden");
  });

  qsa(".c-form__field--error", form).forEach(el => {
    el.classList.remove("c-form__field--error");
  });

  pages.userInfo.state = "default";
  pages.confirm.state = "default";
}

/**
 * Resets date of birth fields on form.
 */
function resetDOBFields() {
  pages.userInfo.fields.day.value = "";
  pages.userInfo.fields.month.value = "";
  pages.userInfo.fields.year.value = "";
}

/**
 * Returns HTML markup for Age Gate wall.
 * @returns {String}
 */
export function getMarkup() {
  const userInfoPgBlurbMarkup = `<div id="${userInfoBlurbId}">
    <p class='c-utility-body--primary__text'><strong>This section of the website is restricted to individuals 19 or older,
      in Canada, outside of the province of Quebec.</strong></p>

    <p class='c-utility-body--primary__text'>Please verify the information below. This information will not be stored beyond this session.</p>
  </div>`;

  const userInfoPgFormMarkup = `
  <form id="${userInfoFormId}" class="c-form" action="#" aria-live="true" autocomplete="off">
    <div id="${userInfoFormId}-global-error" class="c-form__global-error" hidden role="status">Please fill out the required fields and try again.</div>

      <div class="c-form__field c-form__field--select">
        <label for="${userInfoFormId}-province" class="c-form__label">Select your Province/Territory:</label>
        <select id="${userInfoFormId}-province" name="${userInfoFormId}-province" class="c-form__control c-form__control--select">
          <option value="Alberta">Alberta</option>
          <option value="British Columbia">British Columbia</option>
          <option value="Manitoba">Manitoba</option>
          <option value="New Brunswick">New Brunswick</option>
          <option value="Newfoundland and Labrador">Newfoundland and Labrador</option>
          <option value="Northwest Territories">Northwest Territories</option>
          <option value="Nova Scotia">Nova Scotia</option>
          <option value="Nunavut">Nunavut</option>
          <option value="Ontario">Ontario</option>
          <option value="Prince Edward Island">Prince Edward Island</option>
          <option value="Saskatchewan">Saskatchewan</option>
          <option value="Yukon">Yukon</option>
        </select>
        <p class="c-form__error" hidden>Please select your province</p>
      </div>

      <fieldset class="c-form__fieldset" id="${userInfoFormId}-dob">
        <legend class="c-form__legend c-form__label">Please enter your legal date of birth:</legend>

        <div class="c-form__field-wrapper">
          <div class="c-form__field c-form__field c-form__field--text">
            <label for="${userInfoFormId}-day" class="c-form__label u-visually-hidden">Day</label>
            <input id="${userInfoFormId}-day" class="c-form__control c-form__control--text" type="text" placeholder="DD" autocomplete="bday-day">
          </div>

          <div class="c-form__field c-form__field--text">
            <label for="${userInfoFormId}-month" class="c-form__label u-visually-hidden">Month</label>
            <input id="${userInfoFormId}-month" class="c-form__control c-form__control--text" type="text" placeholder="MM" autocomplete="bday-month">
          </div>

          <div class="c-form__field c-form__field--text">
            <label for="${userInfoFormId}-year" class="c-form__label u-visually-hidden">Year</label>
            <input id="${userInfoFormId}-year" class="c-form__control c-form__control--text" type="text" placeholder="YYYY" autocomplete="bday-year">
          </div>
        </div>

        <p id="${userInfoFormId}-dob-error" class="c-form__error" hidden role="status">Please enter your legal date of birth.</p>
      </fieldset>

      <div class="c-form__field c-form__field--button">
        <input id="${userInfoFormId}-verify" class="c-button c-button--utility-green-primary" type="submit" value="Verify">
      </div>
  </form>
`;

  const confirmPgFormMarkup = `
  <div id="${confirmBlurbId}" class="u-hidden">
    <p class='c-utility-body--primary__text'><strong>Please answer the following:</strong></p>
  </div>

  <form id="${confirmFormId}" class="c-form u-hidden" action="#" aria-live="true" autocomplete="off">
      <div id="${confirmFormId}-global-error"  class="c-form__global-error" hidden role="status">Please fill out the required fields and try again.</div>

      <div class="c-form__field c-form__field--checkbox">
        <div class="c-form__field-inner">
          <input id="${confirmFormId}-confirm" class="c-form__control c-form__control--checkbox" type="checkbox" value="confirmed">
          <label for="${confirmFormId}-confirm" class="c-form__label">Yes, I confirm that <span id="${confirmFormId}-user-dob">[userDateOfBirth]</span> is my legal date of birth and I live in <span id="${confirmFormId}-user-province">[province]</span></label>
        </div>
        <p id="${confirmFormId}-confirm-error" class="c-form__error" hidden role="status">Please verify your information.</p>
      </div>

      <div class="c-form__field c-form__field--button">
        <input id="${confirmFormId}-continue"class="c-button c-button--utility-green-primary" type="submit" value="Continue">
      </div>
    </fieldset>
  </form>
`;

  const underAgePgMarkup = `<div class="u-hidden" id="${underAgePgId}" aria-live="true">
  <p class="c-utility-body--primary__text c-utility-body--primary__text--red-700"><strong>Failed to verify age was not 19 or over.</strong></p>
  <p class="c-utility-body--primary__text">You need to be 19 or older to access this part of the website.</p>
  <p class="c-utility-body--primary__text"><a href="https://www.theglobeandmail.com"  id="under-age-pg-link-to-home" class="c-utility-body--primary__link">Return to globeandmail.com</a></p>
  <p class="c-utility-body--primary__text"><a href="#${userInfoFormId}-day" id="under-age-pg-link-to-form" class="c-utility-body--primary__link">Oops, I would like to re-enter my date of birth</a></p>
  </div>`;

  return `${userInfoPgBlurbMarkup}${userInfoPgFormMarkup}${confirmPgFormMarkup}${underAgePgMarkup}`;
};

/**
 * Renders Age-Gate wall pages/steps based on user's responses and tracks outcome.
 */
export default function init() {
  pages = {
    userInfo: {
      state: "default", // Possible values: "default", "error"
      form: getById(userInfoFormId),
      fields: {
        province: getById(`${userInfoFormId}-province`),
        day: getById(`${userInfoFormId}-day`),
        month: getById(`${userInfoFormId}-month`),
        year: getById(`${userInfoFormId}-year`)
      },
      blurb: getById(userInfoBlurbId)
    },
    underAge: {
      blurb: getById(underAgePgId)
    },
    confirm: {
      state: "default", // Possible values: "default", "error"
      form: getById(confirmFormId),
      fields: {
        confirmed: getById(`${confirmFormId}-confirm`)
      },
      blurb: getById(confirmBlurbId)
    }
  };

  // For user info form submission.
  pages.userInfo.form.addEventListener("submit", e => {
    e.preventDefault();
    resetFormErrors(pages.userInfo.form);

    const hasErrors = validateForm(pages.userInfo.form);
    logger.info(`${debugTag} Errors: ${hasErrors}`);

    if (!hasErrors) {
      let age = calculateAge(values.year, values.month, values.day);
      logger.info(`${debugTag} Age: ${age}`);

      if (age >= 19) { // Age >= 19 years old. Show confirmation form.
        userDateOfBirth = values.day + "/" + values.month + "/" + values.year;
        userProvince = values.province;
        showStep("confirm");

        ee.trigger("analytics.trackGTMEvent", [{
          action: "message: confirm dob and location"
        }, "ageGateEvent"]);

      } else { // Not of legal age, show under age page.
        showStep("underAge");

        ee.trigger("analytics.trackGTMEvent", [{
          action: "message: under age"
        }, "ageGateEvent"]);
      }
    }
  });

  // For >=19 years old confirmation form submission.
  pages.confirm.form.addEventListener("submit", e => {
    e.preventDefault();
    resetFormErrors(pages.confirm.form);
    logger.info(`${debugTag} Confirmation form submitted`);
    const hasErrors = validateForm(pages.confirm.form);

    if (!hasErrors) {
      logger.info(`${debugTag} Confirmed over age ${values.confirm}`);
      session.set(`${confirmedKey}`, values.confirm);

      // Track when user passes Age Gate wall.
      ee.trigger("analytics.trackGTMEvent", [{
        action: "passed wall"
      }, "ageGateEvent"]);
      window.location.reload(true);
    }
  });

  // For under age (< 19 years old) page - link click back to re-enter birth date.
  document.addEventListener("click", e => {
    if (!e.target.matches("#under-age-pg-link-to-form")) {
      return;
    }
    e.preventDefault();
    // Reshow user info form, with province still selected but birthday fields reset.
    resetFormErrors(pages.userInfo.form);
    resetDOBFields();
    showStep("reenterDob");
    logger.info(`${debugTag} Re-enter date of birth link clicked ${e.target}`);
  }, false);

  // For under age (< 19 years old) page - link click back to homepage.
  document.addEventListener("click", e => {
    if (!e.target.matches("#under-age-pg-link-to-home")) {
      return;
    }

    // Track when user does not pass Age Gate wall (i.e., back to home link was clicked).
    ee.trigger("analytics.trackGTMEvent", [{
      action: "clicked back to home link"
    }, "ageGateEvent"]);

    logger.info(`${debugTag} Back to home clicked ${e.target}`);
  }, false);
};
